import React, { useRef, useEffect } from "react";

import cx from "classnames";

import { Button } from "../Button";
import { Heading } from "../Heading";
import { Icon } from "../Icon";

import styles from "./Dialog.scss";

const sizeClasses = {
  small: styles.modalSmall,
  medium: styles.modalMedium,
  large: styles.modalLarge,
  full: styles.modalFull,
};

export interface DialogProps {
  onClose?: () => void;
  size?: "small" | "medium" | "large" | "full";
  isOpen?: boolean;
  closeTitle?: string;
  modalTitle?: React.ReactElement | string;
  fullWidthHeader?: React.PropsWithChildren<{}>["children"];
}

export function Dialog({
  isOpen = false,
  onClose,
  closeTitle,
  modalTitle,
  children,
  fullWidthHeader = null,
  size = "medium",
}: React.PropsWithChildren<DialogProps>) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const uuid = React.useId();

  const bodyLockClass = `Dialog__Body--open_${uuid}`;
  const labelledById = `dialog-title-${uuid}`;
  const dialogMaskClassNames = cx(styles.dialogMask, sizeClasses[size]);

  useEffect(() => {
    const doCleanup = () => {
      document.body.classList.remove(bodyLockClass);
    };

    if (isOpen) {
      document.body.classList.add(bodyLockClass);
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
      doCleanup();
    }

    return doCleanup;
  }, [isOpen]);

  const handleClose = () => {
    onClose?.();
  };

  useEffect(() => {
    const handleCancel = (e: Event) => {
      e.preventDefault();
      handleClose();
    };

    dialogRef.current?.addEventListener("cancel", handleCancel);
    return () => {
      dialogRef.current?.removeEventListener("cancel", handleCancel);
    };
  }, []);

  return (
    <dialog
      className={styles.dialog}
      ref={dialogRef}
      aria-labelledby={labelledById}
    >
      <div className={dialogMaskClassNames}>
        <ModalNavigation
          closeTitle={closeTitle}
          onClose={handleClose}
          modalSize={size}
        />
        {fullWidthHeader && (
          <div className={styles.fullWidthHeader}>{fullWidthHeader}</div>
        )}
        {!fullWidthHeader && <div className={styles.closeButtonUnderlay} />}
        <Heading
          className={cx({
            [styles.dialogHeadingReduceTopMargin]: !fullWidthHeader,
          })}
          id={labelledById}
          level="2"
          data-testid="dialog-title"
        >
          {modalTitle}
        </Heading>
        {children}
      </div>
    </dialog>
  );
}

interface ModalNavigationProps {
  closeTitle?: string;
  onClose: () => void;
  modalSize: "small" | "medium" | "large" | "full";
}

const sizeStyles: Record<string, string> = {
  small: styles.smallModalNavigation,
  medium: styles.mediumModalNavigation,
};

function ModalNavigation({
  closeTitle,
  onClose,
  modalSize,
}: ModalNavigationProps) {
  const navigationClassNames = cx(styles.navigation, sizeStyles[modalSize]);

  return (
    <div className={navigationClassNames}>
      <Button
        variant="transparent"
        aria-label={closeTitle}
        onClick={onClose}
        className={styles.closeButton}
        data-testid="close-modal-button"
      >
        <Icon icon="close" className={styles.closeButtonIcon} />
      </Button>
    </div>
  );
}
